import * as React from 'react';
import { __ } from 'react-i18n';
import { DsiAddressesDeliveryList, FlexCol } from '../../..';
import styled from 'styled-components';
import { rem } from 'polished';
import { TitleWithChildren } from '../../Zemplin/General/TitleWithChildren';
import { ContactInfo } from './Form/ContactInfo';
import { BillingAddress } from './Form/BillingAddress';
import { DeliveryAddress } from './Form/DeliveryAddress';
import { FormTextArea } from '../Form/FormTextArea';
import {
  getPhonePrefix,
  getPhoneWithoutPrefix,
  prop,
} from '../../../utilities';
import { InfoText, InfoType } from '../../Zemplin/General/InfoText';
import { FormInput } from '../Form/FormInput';
import { useState } from 'react';
import { BillingAddressB2B } from './Form/BillingAddressB2B';
import { phonePrefixes, validateCartInput } from './utilities';
import { BillingAddressB2C } from './Form/BillingAddressB2C';
import { DsiCartAddressesDeliveryList } from '../MyAccount/CartAddressesDeliveryList';
import { DsiCheckBox as CheckBox } from '../General/Checkbox';
import { Register } from './Form/Register';

interface Props {
  data: any;
  isFetching: boolean;
  updateCart: (data: any, forceData?: boolean) => void;
  deliveryAddresses: any;
  deliveryAddressesIsFetching: boolean;
  showProblems: boolean;
  isB2B: boolean;
  lang: string;
  state: any;
  dispatchState: any;
  formRef: any;
  isB2CUser: boolean;
  disableNonContentsFields?: boolean;
}

function CartContactInfoPart({
  data,
  isFetching,
  updateCart,
  deliveryAddresses,
  deliveryAddressesIsFetching,
  showProblems,
  isB2B,
  lang,
  state,
  dispatchState,
  formRef,
  isB2CUser,
  disableNonContentsFields,
}: Props) {
  const emailRef = React.useRef<HTMLInputElement>(null);
  const phoneRef = React.useRef<HTMLInputElement>(null);
  const [currentId, setCurrentId] = React.useState(
    isB2B
      ? prop(deliveryAddresses, '0.id', null)
      : prop(data, 'delivery_address.delivery_address_id'),
  );
  let updateDataTimeout: any = null;
  const problems = showProblems ? data.problems : null;
  const initialState = getInitialStateFromCartData(data, isB2B, isB2CUser);
  // const [state, dispatch] = React.useReducer(reducer, initialState);
  const foundFormProblems =
    !problems || problems.length === 0
      ? null
      : problems.filter(p => p.type === 'form');

  const formProblems = foundFormProblems
    ? prop(foundFormProblems[0], 'payload')
    : null;

  const {
    use_delivery_address,
    billing_company,
    vat_payer,
    note,
    cart_label,
    register,
  } = state;

  const handleFieldChange = (e: any) => {
    const value = e.target.value;
    const field = e.target.name;
    dispatchState({ field, value });
    if (
      !isB2B &&
      (field === 'use_delivery_address' ||
        field === 'billing_company' ||
        field === 'vat_payer' ||
        field === 'register')
    ) {
      updateCart({ ...data, [field]: value }, true);
    }
  };

  const handleFieldBlur = (e, a) => {
    updateCart({ ...data, ...state });
  };

  // const onBillingBlur = () => undefined;
  const onBillingBlur = (updatedData: any) => {
    let person = { ...data.billing_address.person, ...updatedData.person };
    if (emailRef.current) {
      person = { ...person, email: emailRef.current.value };
    }
    if (phoneRef.current) {
      person = { ...person, phone: phoneRef.current.value };
    }

    const newData = {
      ...data,
      billing_address: {
        ...data.billing_address,
        ...updatedData,
        person,
      },
    };
    updateData(newData);
  };

  const onDeliveryBlur = (updatedData: any) => {
    let person = { ...data.billing_address.person, ...updatedData.person };
    if (emailRef.current) {
      person = { ...person, email: emailRef.current.value };
    }
    if (phoneRef.current) {
      person = { ...person, phone: phoneRef.current.value };
    }

    const newData = {
      ...data,
      delivery_address: {
        ...data.delivery_address,
        ...updatedData,
        person,
      },
    };
    updateData(newData);
  };

  const onCompanyBlur = (updatedData: any) => {
    const newData = {
      ...data,
      company: {
        ...data.company,
        ...updatedData,
      },
    };
    updateData(newData);
  };

  const onContactBlur = (updatedData: any) => {
    const newData = {
      ...data,
      billing_address: {
        ...data.billing_address,
        person: { ...data.billing_address.person, ...updatedData },
      },
      delivery_address: {
        ...data.delivery_address,
        person: { ...data.delivery_address.person, ...updatedData },
      },
    };
    updateData(newData);
  };

  // autofill handler
  const updateData = (newData, timeoutMs = 50) => {
    if (updateDataTimeout) {
      clearTimeout(updateDataTimeout);
    }

    updateDataTimeout = setTimeout(async () => {
      updateCart(newData);
    }, timeoutMs);
  };

  const handleCurrentAddressChange = (id: number) => {
    setCurrentId(id);
    if (id) {
      const delAddress = deliveryAddresses.find(d => d.id === id);
      const name =
        prop(delAddress, 'name', '') ||
        prop(delAddress, 'billing_address.person.name', '') ||
        '';
      const surname =
        prop(delAddress, 'surname', '') ||
        prop(delAddress, 'billing_address.person.surname', '') ||
        '';
      const email =
        prop(delAddress, 'delivery_email', '') ||
        prop(delAddress, 'billing_address.person.email', '') ||
        '';
      const phone =
        prop(delAddress, 'delivery_phone', '') ||
        prop(delAddress, 'billing_address.person.phone', '') ||
        '';
      const person = { name, surname, email, phone };

      const newData = {
        ...data,
        delivery_address: {
          city: prop(delAddress, 'delivery_city', '') || '',
          street: prop(delAddress, 'delivery_street', '') || '',
          street_number: prop(delAddress, 'delivery_number', '') || '',
          zip: prop(delAddress, 'delivery_zip', '') || '',
          country: prop(delAddress, 'country', ''),
          company: prop(delAddress, 'delivery_company', '') || '',
          delivery_address_id: prop(delAddress, 'id', ''),
          person,
        },
      };
      updateData(newData);
    } else {
      const newData = {
        ...data,
        delivery_address: {
          city: '',
          street: '',
          street_number: '',
          zip: '',
          country: '',
          company: '',
          delivery_address_id: 0,
          person: {
            name: '',
            surname: '',
            email: '',
            phone: '',
          },
        },
      };
      updateData(newData);
    }
  };

  return (
    <>
      {/* {formProblems && (
        <InfoText
          maxWidth={1168}
          type={InfoType.ERROR}
          info={`Opravte prosím chyby v košíku`}
        />
      )} */}
      <form ref={formRef}>
        <Wrapper>
          {(!isB2B || !!initialState.email || !!initialState.phone) && (
            <TitleWithChildren
              smallMargin={true}
              title={isB2B ? __('Kontaktné údaje') : __('Dodacie údaje')}
            >
              {!data?.user_id ? (
                <RegistrationWrapper>
                  <CheckBox
                    name="register"
                    selected={register}
                    onClick={handleFieldChange}
                    label={__('Chcem sa registrovať')}
                  />

                  {register ? <Register formProblems={formProblems} /> : <></>}
                </RegistrationWrapper>
              ) : (
                <></>
              )}

              {!isB2B && deliveryAddresses && deliveryAddresses.length > 0 && (
                <DsiCartAddressesDeliveryList
                  onAddressClick={handleCurrentAddressChange}
                  addresses={deliveryAddresses}
                  currentId={currentId}
                  isB2B={isB2B}
                />
              )}
              <ContactInfo
                initialInfo={{
                  email: initialState.email,
                  phonePrefix: getPhonePrefix(initialState.phone),
                  phone: getPhoneWithoutPrefix(initialState.phone),
                }}
                onBlur={onContactBlur}
                formProblems={formProblems}
                emailRef={emailRef}
                phoneRef={phoneRef}
                isB2B={isB2B}
                deliveryEmail={
                  isB2CUser
                    ? prop(data, 'delivery_address.person.email')
                    : undefined
                }
                deliveryPhone={
                  isB2CUser
                    ? getPhoneWithoutPrefix(
                        prop(data, 'delivery_address.person.phone'),
                      )
                    : undefined
                }
              />
            </TitleWithChildren>
          )}
          {isB2B && (
            <TitleWithChildren title={isB2B ? __('Fakturačná adresa') : ''}>
              <BillingAddressB2B
                isCompany={false}
                billingAddress={prop(data, 'billing_address')}
                company={prop(data, 'company')}
                billing_company={billing_company}
                use_delivery_address={use_delivery_address}
                onBillingBlur={onBillingBlur}
                onCompanyBlur={onCompanyBlur}
                handleFieldChange={handleFieldChange}
                formProblems={formProblems}
                isB2B={isB2B}
                lang={lang}
              />
            </TitleWithChildren>
          )}
          {!isB2B && (
            <BillingAddress
              isCompany={false}
              billingAddress={prop(data, 'billing_address')}
              deliveryAddress={prop(data, 'delivery_address')}
              company={prop(data, 'company')}
              billing_company={billing_company}
              use_delivery_address={use_delivery_address}
              onBillingBlur={onBillingBlur}
              onCompanyBlur={onCompanyBlur}
              onDeliveryBlur={onDeliveryBlur}
              handleFieldChange={handleFieldChange}
              formProblems={formProblems}
              isB2B={isB2B}
              isB2C={isB2CUser}
              lang={lang}
              deliveryAddresses={deliveryAddresses}
              vat_payer={vat_payer}
              disableNonContentsFields={disableNonContentsFields}
            />
          )}
          {isB2B && (
            <TitleWithChildren title={__('Dodacia adresa')}>
              <DeliveryAddress
                deliveryAddresses={deliveryAddresses}
                deliveryAddressesIsFetching={deliveryAddressesIsFetching}
                deliveryAddress={prop(data, 'delivery_address')}
                onBlur={onDeliveryBlur}
                formProblems={formProblems}
                isB2B={isB2B}
                lang={lang}
              />
            </TitleWithChildren>
          )}
          {disableNonContentsFields !== true ? (
            <>
              <FormTextArea
                name="note"
                type="text"
                label={__('Poznámka')}
                value={note}
                onChange={handleFieldChange}
                // onBlur={handleFieldBlur}
              />
              <FormInput
                name="cart_label"
                value={cart_label}
                maxWidth={100}
                maxWidthPx={224}
                required={false}
                onChange={handleFieldChange}
                label={__('Vaše číslo objednávky')}
                validMessage={formProblems?.cart_label}
                // onBlur={handleFieldBlur}
              />
            </>
          ) : (
            <></>
          )}
        </Wrapper>
      </form>
    </>
  );
}

function reducer(state, { field, value }) {
  return { ...state, [field]: value };
}

const getInitialStateFromCartData = (data: any, isB2B, isB2C) => {
  return {
    email: isB2B
      ? prop(data, 'billing_address.person.email', '') ||
        prop(data, 'delivery_address.person.email', '')
      : isB2C
      ? prop(data, 'delivery_address.person.email', '')
      : prop(data, 'billing_address.person.email', ''),
    phone: isB2B
      ? prop(data, 'billing_address.person.phone', '') ||
        prop(data, 'delivery_address.person.phone', '')
      : isB2C
      ? prop(data, 'delivery_address.person.phone', '')
      : prop(data, 'billing_address.person.phone', ''),
    use_delivery_address: true,
    billing_company:
      isB2B && prop(data, 'company.ico')
        ? true
        : prop(data, 'billing_company', false),
    note: prop(data, 'note', ''),
    cart_label: prop(data, 'cart_label', ''),
    vat_payer: prop(data, 'vat_payer', false) || false,
  };
};

const Wrapper = styled(FlexCol)`
  max-width: ${rem(688)};
  width: 100%;
`;

const RegistrationWrapper = styled(FlexCol)`
  margin-bottom: ${rem(24)};
`;

export { CartContactInfoPart as DsiCartContactInfoPart };
